import type { Node, Replica } from "@mapcraft-tasks/client-store";
import { containerRows } from "@mapcraft-tasks/client-store";

/**
 * Плоский список видимых строк — то, по чему ходят стрелки и что рисует аутлайн.
 *
 * Строка ≠ нода: одна нода видна в нескольких местах (родной дом + вплетения),
 * поэтому ключ строки — ПУТЬ, а не id. Иначе свёртка одного вхождения схлопывала бы
 * все остальные, а выделение прыгало бы в чужой контейнер (ui-spec 3.3).
 */
export type Row = {
	/** Уникален в пределах экрана: `containerKey/nodeId`. */
	key: string;
	nodeId: string;
	node: Node;
	depth: number;
	containerId: string;
	/** Не null — строка является вплетением; `Del` уберёт вплетение, а не ноду. */
	placementId: string | null;
	hasChildren: boolean;
	expanded: boolean;
	/**
	 * Нода уже встречалась выше по ЭТОМУ пути. Циклы вплетений разрешены моделью
	 * (placement = симлинк), поэтому защита обязана стоять на рендере: строка рисуется,
	 * но не раскрывается (ui-spec 8.3).
	 */
	cyclic: boolean;
};

export type FlattenOptions = {
	replica: Replica;
	rootId: string;
	isCollapsed: (key: string) => boolean;
	/** Ограничение глубины для `ec0…ec9`; `Infinity` — обычный режим. */
	maxDepth?: number;
};

export function flatten(options: FlattenOptions): Row[] {
	const { replica, rootId, isCollapsed } = options;
	const maxDepth = options.maxDepth ?? Number.POSITIVE_INFINITY;
	const out: Row[] = [];

	const walk = (containerId: string, containerKey: string, depth: number, ancestors: ReadonlySet<string>) => {
		if (depth > maxDepth) {
			return;
		}
		for (const entry of containerRows(replica, containerId)) {
			const node = replica.nodes.get(entry.id);
			if (!node) {
				continue;
			}
			const key = `${containerKey}/${entry.id}`;
			const cyclic = ancestors.has(entry.id);
			const hasChildren = !cyclic && containerRows(replica, entry.id).length > 0;
			const expanded = hasChildren && !isCollapsed(key);

			out.push({
				key,
				nodeId: entry.id,
				node,
				depth,
				containerId,
				placementId: entry.placementId,
				hasChildren,
				expanded,
				cyclic
			});

			if (expanded) {
				walk(entry.id, key, depth + 1, new Set([...ancestors, entry.id]));
			}
		}
	};

	walk(rootId, "", 0, new Set([rootId]));
	return out;
}

/** Соседи строки в её контейнере — по ним ходят `Tab`, `Cmd+↑/↓` и расширение выделения. */
export function siblingsOf(rows: readonly Row[], row: Row): Row[] {
	return rows.filter((r) => r.containerId === row.containerId && r.depth === row.depth);
}

/**
 * Ключ контейнера строки — тот же путь без последнего сегмента. У строки верхнего уровня
 * ключ вида `/nodeId`, и контейнером оказывается пустая строка — ровно тот `containerKey`,
 * с которого `flatten` начинает обход.
 */
export function containerKeyOf(key: string): string {
	return key.slice(0, key.lastIndexOf("/"));
}

export function indexOfKey(rows: readonly Row[], key: string | null): number {
	return key === null ? -1 : rows.findIndex((r) => r.key === key);
}
